import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Link } from '@mui/material';
import { CREDIT_LIMITS } from '../utils/constants.js';
import CreditChip from './CreditChip.js';

/**
 * ProgramProgress component for displaying total credit progress and copy link functionality
 * @param {number} totalCredits - Total selected credits
 * @param {Function} onCopyLink - Function to handle copying the link
 */
const ProgramProgress = ({ totalCredits, onCopyLink }) => {
  const [buttonState, setButtonState] = useState('idle'); // 'idle', 'copying', 'copied', 'error'

  const handleReset = () => {
    // Navigate to the base URL without any query parameters to reset all choices
    window.location.href = window.location.pathname;
  };

  // Simple color function for total credits
  const getTotalCreditsColor = (credits) => {
    if (credits >= CREDIT_LIMITS.TOTAL_REQUIRED) return 'success'; // Green when 120+ (stays green)
    if (credits >= CREDIT_LIMITS.TOTAL_REQUIRED * 0.8) return 'primary'; // Blue when close (96-119)
    return 'default'; // Gray when starting (0-95)
  };

  const handleCopyClick = () => {
    setButtonState('copying');
    
    onCopyLink().then(() => {
      setButtonState('copied');
      // Reset to idle after 2 seconds
      setTimeout(() => {
        setButtonState('idle');
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy link: ', err);
      setButtonState('error');
      // Reset to idle after 2 seconds
      setTimeout(() => {
        setButtonState('idle');
      }, 2000);
    });
  };

  // Get button text and color based on state
  const getButtonProps = () => {
    switch (buttonState) {
      case 'copying':
        return { text: 'COPYING...', color: 'primary', disabled: true };
      case 'copied':
        return { text: 'COPIED!', color: 'success', disabled: true };
      case 'error':
        return { text: 'FAILED', color: 'error', disabled: true };
      default:
        return { text: 'Copy Link', color: 'primary', disabled: false };
    }
  };

  const buttonProps = getButtonProps();

  return (
    <>
      {/* Total Credits Display and Copy Link */}
      <Paper elevation={2} sx={{ p: 3, mt: 4 }}>
        <Box 
          display={{ xs: 'flex', md: 'grid' }}
          flexDirection={{ xs: 'column' }}
          gridTemplateColumns={{ md: '1fr 1fr 1fr' }}
          alignItems="center"
          gap={2}
        >
          <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }}>
            <Link
              component="button"
              variant="h5"
              color="primary"
              onClick={handleReset}
              sx={{
                textDecoration: 'none',
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                padding: 0,
                font: 'inherit',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Reset All Choices
            </Link>
          </Box>
          
          <Box display="flex" justifyContent="center">
            <CreditChip 
              credits={totalCredits}
              maxCredits={CREDIT_LIMITS.TOTAL_REQUIRED}
              type="total"
              getColorFn={getTotalCreditsColor}
              variant="filled"
              size="large"
            />
          </Box>
          
          <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-end' }}>
            <Button 
              variant="contained" 
              color={buttonProps.color}
              size="large"
              disabled={buttonProps.disabled}
              sx={{ 
                px: 4, 
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                boxShadow: buttonProps.disabled ? 1 : 3,
                width: '180px', // Fixed width to prevent layout shift between states
                '&:hover': {
                  boxShadow: buttonProps.disabled ? 1 : 6,
                  transform: buttonProps.disabled ? 'none' : 'translateY(-1px)'
                },
                '&.Mui-disabled': {
                  color: 'white',
                  opacity: 0.9
                },
                transition: 'all 0.2s ease-in-out'
              }}
              onClick={handleCopyClick}
            >
              {buttonProps.text}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Author Signature */}
      <Box 
        display="flex" 
        justifyContent="center" 
        mt={2} 
        mb={0}
      >
        <Typography 
          variant="caption" 
          color="textSecondary"
          sx={{ 
            fontStyle: 'italic',
            opacity: 0.7
          }}
        >
          Vibe coded by Petr Kudláček, 2025
        </Typography>
      </Box>
    </>
  );
};

export default ProgramProgress; 